import type React from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import { colorsPalettes, setFont } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";

import { useFocus } from "app/custom-hooks/useFocus";
import { i18nFilter } from "app/filters/i18nFilter";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

const Input = styled.input`
    width: 86px;
    height: 29px;
    padding-left: 10px;
    border: 1px solid ${colorsPalettes.carbon[100]};
    ${setFont({ $size: 14, $color: colorsPalettes.carbon[500] })}
    &::placeholder {
        ${setFont({ $size: 14, $color: colorsPalettes.carbon[300] })};
    }
`;
const Container = styled.div`
    height: 124px;
`;
const Text = styled.label`
    display: inline-block;
    cursor: inherit;
    width: fit-content;
    ${setFont({ $size: 14, $color: colorsPalettes.carbon[500] })}
`;

const InputsRowContainer = styled.div`
    display: flex;
    padding: 20px 14px;
    justify-content: space-between;
`;
const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ButtonsContainer = styled.div`
    padding: 0 14px;
    display: flex;
    justify-content: flex-end;
`;

const ActionButtons = styled(Button)`
    margin-left: 10px;
    button {
        cursor: pointer;
    }
`;

export interface IRangeValues {
    fromValue: string;
    toValue: string;
}

export interface IRangeFilterProps {
    fromButtonText?: string;
    toButtonText?: string;

    fromPlaceHolder?: string;
    toPlaceHolder?: string;

    submitButtonText?: string;
    clearButtonText?: string;

    initialFromValue?: string;
    initialToValue?: string;

    onClearCallback?: () => void;
    onDoneCallback: (rangeValues: IRangeValues) => void;
    onValueChange?: (rangeValues: IRangeValues) => void;

    valuesFormatter?: (value: string) => string;
    valuesParser?: (value: string) => string;

    isValidRangeValues?: (fromValue: string, toValue: string) => boolean;

    canSubmit?: boolean;

    minValue?: number;
    maxValue?: number;
}

const defaultValuesFormatter = (value: string) => value && String(value).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
const defaultValuesParser = (value: string) => value && value.replace(/,/g, String());

export const RangeFilter: React.FC<IRangeFilterProps> = (props) => {
    const {
        fromButtonText = i18n("common.from"),
        toButtonText = i18n("common.to"),
        fromPlaceHolder = "0",
        toPlaceHolder = ">1,000,000",
        submitButtonText = i18n("common.done"),
        clearButtonText = i18n("common.clear"),
        initialFromValue = String(),
        initialToValue = String(),
        onDoneCallback,
        onClearCallback,
        valuesFormatter = defaultValuesFormatter,
        valuesParser = defaultValuesParser,
        isValidRangeValues,
        onValueChange,
        canSubmit = true,
        minValue,
        maxValue,
    } = props;

    const [rangeValues, setRangeValues] = useState<IRangeValues>({
        fromValue: initialFromValue,
        toValue: initialToValue,
    });
    const { htmlElementReference, setFocus } = useFocus();

    useEffect(setFocus, []);

    const { fromValue, toValue } = rangeValues;
    const showClearButton = Boolean(fromValue) || Boolean(toValue);

    const getValidatedMinMaxValue = (value: string | number): string => {
        const incomingValue = Number(value);

        if (typeof minValue === "number") {
            if (incomingValue < minValue) {
                return String(minValue);
            }
        }

        if (typeof maxValue === "number") {
            if (incomingValue > maxValue) {
                return String(maxValue);
            }
        }

        return String(value);
    };

    const validateHandler = (newRangeValues: IRangeValues) => {
        const { fromValue, toValue } = newRangeValues;
        if (isNaN(Number(fromValue)) || isNaN(Number(toValue))) {
            return;
        }

        if (isValidRangeValues) {
            const isValidRange = isValidRangeValues(fromValue, toValue);
            if (!isValidRange) {
                return;
            }
        }

        setRangeValues({ fromValue: getValidatedMinMaxValue(fromValue), toValue: getValidatedMinMaxValue(toValue) });
        onValueChange?.(newRangeValues);
    };
    const onFromValueChange = (event: FixmeType) =>
        validateHandler({ fromValue: valuesParser(event.target.value), toValue });
    const onToValueChange = (event: FixmeType) =>
        validateHandler({ fromValue, toValue: valuesParser(event.target.value) });

    const onClearButtonClick = () => {
        setRangeValues({ fromValue: String(), toValue: String() });
        onClearCallback?.();
        setFocus();
    };
    const onDoneButtonClick = () => {
        if (toValue && fromValue && Number(toValue) < Number(fromValue)) {
            const newRangeValues = { fromValue: toValue, toValue: fromValue };
            onDoneCallback(newRangeValues);
            setRangeValues(newRangeValues);
            return;
        }
        onDoneCallback(rangeValues);
    };

    const getInputValue = (value: string) => (initialFromValue || initialToValue ? valuesFormatter(value) : value);

    const onKeyDown = (event: FixmeType) => {
        const { keyCode } = event;
        const ENTER_KEY_CODE = 13;
        if (keyCode === ENTER_KEY_CODE) {
            onDoneButtonClick();
        }
    };
    return (
        <Container
            className="range-filter"
            onKeyDown={onKeyDown}
        >
            <InputsRowContainer>
                <TextContainer>
                    <Text>{fromButtonText}</Text>
                </TextContainer>
                <Input
                    ref={htmlElementReference}
                    placeholder={fromPlaceHolder}
                    value={getInputValue(fromValue)}
                    onChange={onFromValueChange}
                />
                <TextContainer>
                    <Text>{toButtonText}</Text>
                </TextContainer>
                <Input
                    placeholder={toPlaceHolder}
                    value={getInputValue(toValue)}
                    onChange={onToValueChange}
                />
            </InputsRowContainer>
            <ButtonsContainer>
                {showClearButton ? (
                    <ActionButtons
                        type={buttonTypes.FLAT}
                        onClick={onClearButtonClick}
                    >
                        {clearButtonText}
                    </ActionButtons>
                ) : null}
                <ActionButtons
                    onClick={onDoneButtonClick}
                    isDisabled={!canSubmit}
                >
                    {submitButtonText}
                </ActionButtons>
            </ButtonsContainer>
        </Container>
    );
};

const i18n = i18nFilter();
